import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../Avatar';
import { useAddTeamMember, useTeamUsers } from '../../../hooks/useTeams';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import type { Role } from '../../../services/roles';
import type { UserListItem } from '../../../services/users';

interface AddTeamMemberFormProps {
    team: Role;
    existingMemberIds: readonly string[];
    onCancel: () => void;
}

function matchesQuery(user: UserListItem, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return user.fullname.toLowerCase().includes(q) || user.email.toLowerCase().includes(q);
}

// Search-then-pick list over the scope's user pool, same interaction as
// RolePicker - a persistent filtered list rather than a closed combobox.
// Stays open after a pick so several members can be added in one sitting;
// the freshly-added user drops off the list once the members query refetches.
export function AddTeamMemberForm({ team, existingMemberIds, onCancel }: AddTeamMemberFormProps) {
    const { showToast } = useToast();
    const { users, isLoading } = useTeamUsers(team.scope);
    const { mutateAsync: addMember, isPending } = useAddTeamMember();
    const [search, setSearch] = useState('');
    const [addingUserId, setAddingUserId] = useState<string | null>(null);

    const available = useMemo(() => {
        const existing = new Set(existingMemberIds);
        return users.filter((user) => !existing.has(user.id) && matchesQuery(user, search));
    }, [users, existingMemberIds, search]);

    const handleAdd = async (userId: string) => {
        setAddingUserId(userId);
        try {
            await addMember({ teamId: team.id, userId });
            showToast('Member added', 'success');
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to add member'), 'error');
        } finally {
            setAddingUserId(null);
        }
    };

    return (
        <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-2">
                <span className="h-9 flex-1 border border-gray-300 rounded-lg px-3 flex items-center gap-2 bg-white focus-within:border-gray-800 focus-within:ring-2 focus-within:ring-gray-200">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name or email"
                        autoFocus
                        className="flex-1 min-w-0 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
                    />
                </span>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-xs font-semibold text-gray-500 hover:text-gray-700 px-2 shrink-0"
                >
                    Done
                </button>
            </div>
            <div className="border border-gray-200 rounded-lg bg-white overflow-hidden divide-y divide-gray-100 max-h-56 overflow-y-auto">
                {isLoading && (
                    <div className="px-3 py-3 text-[12px] text-gray-400">Loading users…</div>
                )}
                {!isLoading && available.length === 0 && (
                    <div className="px-3 py-3 text-[12px] text-gray-400">No users found.</div>
                )}
                {!isLoading &&
                    available.map((user) => {
                        const isAdding = isPending && addingUserId === user.id;
                        return (
                            <button
                                key={user.id}
                                type="button"
                                disabled={isAdding}
                                onClick={() => {
                                    void handleAdd(user.id);
                                }}
                                className="w-full px-3 py-2 flex items-center gap-2.5 text-left hover:bg-gray-50 disabled:opacity-50"
                            >
                                <div className="w-7 h-7 shrink-0">
                                    <Avatar user={{ name: user.fullname, email: user.email }} />
                                </div>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-[13px] font-semibold text-gray-800 truncate">
                                        {user.fullname || user.email}
                                    </span>
                                    <span className="block text-[11px] text-gray-500 truncate">
                                        {user.email}
                                    </span>
                                </span>
                                {isAdding && (
                                    <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin shrink-0" />
                                )}
                            </button>
                        );
                    })}
            </div>
        </div>
    );
}
